import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Reply, CheckCircle2, Archive, Send, X, Loader2 } from "lucide-react";
import SafeHtml from "@/components/SafeHtml";

const TYPES = ["", "assessment", "quote", "contact", "privacy_check", "campaign_application", "consultation"];
const STATUSES = ["", "new", "in_progress", "done", "archived", "requested", "confirmed", "completed", "cancelled"];

const STATUS_CLASS = {
  new: "bg-gold text-navy",
  in_progress: "bg-navy/10 text-navy",
  done: "bg-emerald-100 text-emerald-800",
  archived: "bg-navy/5 text-navy/60",
  requested: "bg-amber-100 text-amber-800",
  confirmed: "bg-sky-100 text-sky-800",
  completed: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-red-100 text-red-700",
};

export default function AdminInbox() {
  const [items, setItems] = useState([]);
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [selected, setSelected] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loadingReplies, setLoadingReplies] = useState(false);

  const [replyOpen, setReplyOpen] = useState(false);
  const [replySubject, setReplySubject] = useState("");
  const [replyBody, setReplyBody] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => {
    const q = new URLSearchParams();
    if (type) q.set("type", type);
    if (status) q.set("status", status);
    api.get(`/admin/submissions?${q}`).then((r) => setItems(r.data));
  };
  useEffect(() => { load(); }, [type, status]);

  const openDetail = async (s) => {
    setSelected(s);
    setLoadingReplies(true);
    try {
      const r = await api.get(`/admin/submissions/${s.id}/replies`);
      setReplies(r.data || []);
    } catch (_) {
      setReplies([]);
    } finally {
      setLoadingReplies(false);
    }
  };

  const closeDetail = () => {
    setSelected(null);
    setReplies([]);
    setReplyOpen(false);
    setReplySubject("");
    setReplyBody("");
  };

  const updateStatus = async (id, newStatus) => {
    try {
      await api.put(`/admin/submissions/${id}`, { status: newStatus });
      toast.success(`Status → ${newStatus}`);
      setSelected((prev) => (prev ? { ...prev, status: newStatus } : prev));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const startReply = () => {
    const preset = selected.subject
      ? `Re: ${selected.subject}`
      : "Regarding your inquiry — HAWKAM Consulting";
    setReplySubject(preset);
    setReplyBody("");
    setReplyOpen(true);
  };

  const sendReply = async () => {
    if (!replySubject.trim() || !replyBody.trim()) {
      toast.error("Subject and message are required");
      return;
    }
    setSending(true);
    try {
      const r = await api.post(`/admin/submissions/${selected.id}/reply`, {
        subject: replySubject,
        body_html: replyBody,
      });
      const reply = r.data;
      if (reply?.delivery_status === "failed") {
        toast.error(`Delivery failed: ${reply.error || "unknown error"}`);
      } else {
        toast.success(`Reply sent to ${selected.email}`);
      }
      const rr = await api.get(`/admin/submissions/${selected.id}/replies`);
      setReplies(rr.data || []);
      // Refresh the selected row's status (backend auto-flips new → in_progress)
      const listResp = await api.get(`/admin/submissions?${new URLSearchParams({
        ...(type ? { type } : {}),
        ...(status ? { status } : {}),
      })}`);
      setItems(listResp.data);
      const refreshed = listResp.data.find((x) => x.id === selected.id);
      if (refreshed) setSelected(refreshed);
      setReplyOpen(false);
      setReplySubject("");
      setReplyBody("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="admin-inbox">
      <span className="eyebrow">Unified Inbox</span>
      <h1 className="mt-2 text-3xl text-navy">Submissions</h1>
      <span className="divider-gold" />

      <div className="flex flex-wrap gap-3 mb-5">
        <Select value={type || "all"} onValueChange={(v) => setType(v === "all" ? "" : v)}>
          <SelectTrigger className="w-48" data-testid="inbox-filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {TYPES.filter(Boolean).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="w-48" data-testid="inbox-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.filter(Boolean).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="card-elev overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-navy text-cream">
            <tr>
              <th className="text-start p-3">Date</th>
              <th className="text-start p-3">Type</th>
              <th className="text-start p-3">Name</th>
              <th className="text-start p-3">Email</th>
              <th className="text-start p-3">Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} data-testid={`sub-row-${s.id}`} className="border-t border-navy/10 hover:bg-cream">
                <td className="p-3 text-navy/80 whitespace-nowrap">{new Date(s.created_at).toLocaleString()}</td>
                <td className="p-3"><Badge variant="secondary">{s.type}</Badge></td>
                <td className="p-3 text-navy">{s.name}</td>
                <td className="p-3 text-navy/80">{s.email}</td>
                <td className="p-3"><Badge className={STATUS_CLASS[s.status] || ""}>{s.status}</Badge></td>
                <td className="p-3 text-end">
                  <button
                    data-testid={`sub-view-${s.id}`}
                    onClick={() => openDetail(s)}
                    className="text-navy underline decoration-gold underline-offset-4"
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-navy/50">No submissions</td></tr>}
          </tbody>
        </table>
      </div>

      {selected && (
        <div
          className="fixed inset-0 bg-navy/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={closeDetail}
        >
          <div
            className="bg-white max-w-3xl w-full rounded-md border border-gold/30 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="sub-detail"
          >
            <div className="p-8 border-b border-navy/10">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-xs uppercase tracking-widest text-navy/60">{selected.type}</div>
                  <h2 className="mt-1 text-2xl text-navy">{selected.name || "Unnamed"}</h2>
                  <div className="text-navy/70 text-sm mt-1">
                    <a href={`mailto:${selected.email}`} className="hover:text-gold">{selected.email}</a>
                    {selected.phone ? ` · ${selected.phone}` : ""}
                  </div>
                  {selected.company && <div className="text-navy/70 text-sm">{selected.company}</div>}
                </div>
                <button onClick={closeDetail} className="text-navy/50 hover:text-navy" data-testid="sub-close">
                  <X size={20} />
                </button>
              </div>
              <span className="divider-gold" />
              {selected.type === "consultation" && (
                <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm" data-testid="consult-details">
                  <div>
                    <div className="text-xs text-navy/50 uppercase tracking-widest">Reference</div>
                    <div className="text-navy font-mono text-xs">HAWKAM-{(selected.id || "").slice(0, 8)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-navy/50 uppercase tracking-widest">Appointment</div>
                    <div className="text-navy">
                      {selected.appointment_at
                        ? new Date(selected.appointment_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
                        : "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-navy/50 uppercase tracking-widest">Service</div>
                    <div className="text-navy">
                      {(() => {
                        const s = selected.service || "";
                        if (s.startsWith("assessment:")) return null; // rendered separately
                        return s || "—";
                      })()}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-navy/50 uppercase tracking-widest">Assessment link</div>
                    <div className="text-navy">
                      {(() => {
                        const s = selected.service || "";
                        if (s.startsWith("assessment:")) {
                          const parts = s.slice("assessment:".length).split("+");
                          return <><code className="text-gold">{parts[0].slice(0, 8)}</code>{parts[1] ? <> · {parts[1]}</> : null}</>;
                        }
                        return "—";
                      })()}
                    </div>
                  </div>
                </div>
              )}
              {selected.subject && (
                <div className="mb-3">
                  <div className="text-xs text-navy/50 uppercase tracking-widest mb-1">Subject</div>
                  <div className="text-navy font-medium">{selected.subject}</div>
                </div>
              )}
              {selected.message && (
                <div className="mb-4">
                  <div className="text-xs text-navy/50 uppercase tracking-widest mb-1">Message</div>
                  <div className="text-navy/80 text-sm whitespace-pre-wrap">{selected.message}</div>
                </div>
              )}
              {selected.answers && (
                <details className="text-sm">
                  <summary className="cursor-pointer text-navy/70">Full payload</summary>
                  <pre className="text-xs text-navy/80 whitespace-pre-wrap font-mono mt-2 p-3 bg-cream rounded">
                    {JSON.stringify(selected.answers, null, 2)}
                  </pre>
                </details>
              )}
            </div>

            {/* Reply thread */}
            <div className="px-8 py-6 bg-cream/40 border-b border-navy/10">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs uppercase tracking-widest text-navy/60">
                  Conversation ({replies.length})
                </div>
                <button
                  data-testid="sub-reply-open"
                  onClick={startReply}
                  disabled={!selected.email}
                  className="btn-gold text-xs disabled:opacity-50"
                >
                  <Reply size={14} /> Reply
                </button>
              </div>
              {loadingReplies ? (
                <div className="text-navy/50 text-sm">Loading…</div>
              ) : replies.length === 0 ? (
                <div className="text-navy/50 text-sm italic">No replies yet.</div>
              ) : (
                <div className="space-y-4">
                  {replies.map((r) => (
                    <div key={r.id} data-testid={`reply-${r.id}`} className="bg-white p-4 rounded border border-navy/10">
                      <div className="flex items-center justify-between text-xs">
                        <div className="text-navy/70">
                          <span className="font-semibold">{r.sent_by}</span> → {r.to_email}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-navy/50">{new Date(r.created_at).toLocaleString()}</span>
                          <Badge className={r.delivery_status === "sent" ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}>
                            {r.delivery_status}
                          </Badge>
                        </div>
                      </div>
                      <div className="mt-2 font-medium text-navy">{r.subject}</div>
                      <SafeHtml
                        className="mt-2 text-sm text-navy/80 prose prose-sm max-w-none"
                        html={r.body_html}
                      />
                      {r.error && <div className="mt-2 text-xs text-red-600">Error: {r.error}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Reply composer */}
            {replyOpen && (
              <div className="px-8 py-6 border-b border-navy/10" data-testid="reply-composer">
                <div className="text-xs uppercase tracking-widest text-navy/60 mb-2">Compose reply</div>
                <input
                  data-testid="reply-subject"
                  className="field-box mb-3"
                  value={replySubject}
                  onChange={(e) => setReplySubject(e.target.value)}
                  placeholder="Subject"
                />
                <textarea
                  data-testid="reply-body"
                  className="field-box min-h-[180px]"
                  value={replyBody}
                  onChange={(e) => setReplyBody(e.target.value)}
                  placeholder="Write your reply — HTML supported (e.g., <p>Hello…</p>). The message is auto-wrapped in the Hawkam brand template."
                />
                <div className="text-xs text-navy/50 mt-1">
                  Sent from <b>HAWKAM Consulting</b> with reply-to set to your admin email.
                </div>
                <div className="flex items-center gap-3 mt-4">
                  <button
                    data-testid="reply-send"
                    onClick={sendReply}
                    disabled={sending}
                    className="btn-gold disabled:opacity-50"
                  >
                    {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                    {sending ? "Sending…" : "Send Reply"}
                  </button>
                  <button
                    onClick={() => setReplyOpen(false)}
                    className="btn-outline-navy"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Status action bar */}
            <div className="px-8 py-5 flex flex-wrap items-center gap-3">
              <div className="text-xs uppercase tracking-widest text-navy/60 mr-2">Status</div>
              <button onClick={() => updateStatus(selected.id, "in_progress")} className="btn-outline-navy text-xs" data-testid="sub-mark-progress">
                <Loader2 size={13} /> In Progress
              </button>
              <button onClick={() => updateStatus(selected.id, "done")} className="btn-gold text-xs" data-testid="sub-mark-done">
                <CheckCircle2 size={13} /> Mark Done
              </button>
              <button onClick={() => updateStatus(selected.id, "archived")} className="btn-outline-navy text-xs" data-testid="sub-archive">
                <Archive size={13} /> Archive
              </button>
              <div className="grow" />
              <Badge className={STATUS_CLASS[selected.status] || ""}>{selected.status}</Badge>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
